import type { TimeDay, TimelineEvent } from "../api/types";
import { isKitEvent, isManualEvent, kitVersion } from "./timeSource";
import { localDayKey } from "./date";
import { boardDay, type BoardDayActivity } from "./weekBoard";

/** Events regroupés par jour local de `occurred_at`. */
export function groupByLocalDay(
  events: TimelineEvent[],
): Map<string, TimelineEvent[]> {
  const m = new Map<string, TimelineEvent[]>();
  for (const ev of events) {
    const key = localDayKey(new Date(ev.occurred_at));
    const arr = m.get(key) ?? [];
    arr.push(ev);
    m.set(key, arr);
  }
  return m;
}

/** Réception la plus récente (max `recorded_at`) : c'est elle qui dit si les
 * données de la nuit sont arrivées, pas l'heure des pointages eux-mêmes. */
export function latestRecordedAt(events: TimelineEvent[]): Date | null {
  let max = Number.NEGATIVE_INFINITY;
  for (const ev of events) {
    const t = Date.parse(ev.recorded_at);
    if (t > max) max = t;
  }
  return Number.isFinite(max) ? new Date(max) : null;
}

export interface DayRecap {
  hasEvents: boolean;
  /** Minutes d'activité, comptées comme la board de la feuille de temps. */
  workedMin: number;
  /** Premier et dernier pointage (minutes du jour), null sans pointage. */
  firstMin: number | null;
  lastMin: number | null;
  /** Activités du jour, de la plus longue à la plus courte. */
  activities: BoardDayActivity[];
  receivedAt: Date | null;
}

function minOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/** Synthèse d'une journée passée d'un collaborateur : minutes et activités du
 * calcul serveur (`timeDay`), horaires et fraîcheur des `events` de ce jour. */
export function recapDay(
  dayKey: string,
  events: TimelineEvent[],
  timeDay: TimeDay | undefined,
): DayRecap {
  const board = boardDay(dayKey, timeDay);
  // Amplitude = premier et dernier pointage réels (arrivée, départ…), pas celle
  // des blocs d'activité : une présence sans activité déclarée a quand même un
  // début et une fin. Les pings de position ne disent rien des horaires.
  const mins = events
    .filter((ev) => ev.event_type !== "location_ping")
    .map((ev) => minOfDay(ev.occurred_at));
  return {
    hasEvents: events.length > 0,
    workedMin: board.workedMin,
    firstMin: mins.length ? Math.min(...mins) : null,
    lastMin: mins.length ? Math.max(...mins) : null,
    activities: board.activities,
    receivedAt: latestRecordedAt(events),
  };
}

export interface ProjectShare {
  label: string;
  minutes: number;
}

export interface ProjectSplit {
  /** Projets de la journée, du plus long au plus court. */
  projects: ProjectShare[];
  /** Minutes imputées à aucun projet. null quand TAWA ne découpe pas encore
   * par projet : rien ne permet alors de le dire. */
  unassignedMin: number | null;
}

/** Répartition par projet d'une journée, d'après les parts calculées par TAWA. */
export function projectSplit(timeDay: TimeDay | undefined): ProjectSplit {
  if (!timeDay?.project_lines) return { projects: [], unassignedMin: null };
  const byProject = new Map<string, number>();
  let unassignedMin = 0;
  for (const part of timeDay.project_lines) {
    if (part.worked_minutes <= 0) continue;
    if (!part.project_id) {
      unassignedMin += part.worked_minutes;
      continue;
    }
    const label = part.project_label || part.project_id;
    byProject.set(label, (byProject.get(label) ?? 0) + part.worked_minutes);
  }
  const projects = [...byProject.entries()]
    .map(([label, minutes]) => ({ label, minutes }))
    .sort((a, b) => b.minutes - a.minutes);
  return { projects, unassignedMin };
}

export interface DayProvenance {
  /** Dernière réception d'un pointage du kit, null si le kit n'a rien poussé. */
  kitReceivedAt: Date | null;
  kitVersion: string | null;
  /** Pointages saisis à la main (ajouts et corrections). */
  manualCount: number;
}

/** D'où viennent les pointages d'une journée : le kit, la main. */
export function dayProvenance(events: TimelineEvent[]): DayProvenance {
  const kit = events.filter(isKitEvent);
  const latest = kit.reduce<TimelineEvent | null>(
    (acc, ev) =>
      !acc || Date.parse(ev.recorded_at) > Date.parse(acc.recorded_at)
        ? ev
        : acc,
    null,
  );
  return {
    kitReceivedAt: latestRecordedAt(kit),
    kitVersion: latest ? kitVersion(latest) : null,
    manualCount: events.filter(
      (ev) => ev.event_type !== "location_ping" && isManualEvent(ev),
    ).length,
  };
}
